import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { toast } from 'sonner'
import { errorText } from '@/lib/utils'
import { api } from './client'
import type {
  AuthResult,
  CatalogVehicle,
  DriverAnalytics,
  DriverSession,
  MapResult,
  NotificationPrefs,
  RfidBalance,
  StartResult,
  User,
  Vehicle,
  WalletTransaction,
} from './types'

/** Endpoints a driver uses. All exist on the API today. */

export const auth = {
  login: (identifier: string, password: string) => api.post<AuthResult>('/auth/login', { identifier, password }),
  me: () => api.get<User>('/auth/me'),
  logout: () => api.post('/auth/logout'),
  /** Sign-up step 1: SMS a code. Fails if the number is already registered. */
  sendOtp: (phone: string) => api.post<{ phone: string }>('/auth/send-otp', { phone }),
  verifyOtp: (phone: string, otp: string) => api.post<{ verification_token: string }>('/auth/verify-otp', { phone, otp }),
  register: (body: { verification_token: string; password: string; full_name?: string; email?: string }) => api.post<AuthResult>('/auth/register', body),
}

export interface MapQuery {
  search?: string
  has_available_connectors?: boolean
  min_power_kw?: number
  latitude?: number
  longitude?: number
  radius_km?: number
  sort_by?: 'name' | 'distance'
  station_id?: number
  per_page?: number
}

export const map = {
  async stations(q: MapQuery = {}, signal?: AbortSignal): Promise<MapResult> {
    const stations = new Map<number, MapResult['stations'][number]>()
    let first: MapResult | undefined
    for (let page = 1; ; page++) {
      const result = await api.get<MapResult>('/public/map-chargers', { per_page: 100, ...q, page }, { signal })
      first ??= result
      if (result.pagination.page !== page || !Number.isInteger(result.pagination.total_pages) || result.pagination.total_pages < 0) {
        throw new Error('Invalid station pagination. Please try again.')
      }
      for (const station of result.stations) stations.set(station.id, station)
      if (page >= result.pagination.total_pages) break
      if (!result.stations.length) throw new Error('Incomplete station results. Please try again.')
    }
    return { ...first, stations: [...stations.values()] }
  },
}

export const driver = {
  balance: () => api.get<RfidBalance>('/user/rfid-balance'),
  analytics: () => api.get<DriverAnalytics>('/user/analytics'),
  activity: () => api.get<{ sessions: DriverSession[]; transactions: WalletTransaction[] }>('/user/activity'),
  active: () => api.get<{ active_sessions: DriverSession[]; total: number }>('/user/active-sessions'),
  history: (q: { page?: number; per_page?: number; status_filter?: string }) =>
    api.get<{ sessions: DriverSession[]; total: number; page: number; per_page: number; total_pages: number }>('/user/sessions/history', q),
  /** connector_id is the connector number; the card pays from its operator's wallet (or Saadhan's for a super card). */
  start: (chargerId: string, connectorNumber: number, cardId: number) =>
    api.post<StartResult>(`/user/chargers/${encodeURIComponent(chargerId)}/remote-start`, undefined, { query: { connector_id: connectorNumber, rfid_card_id: cardId } }),
  stop: (sessionId: number) => api.post(`/user/sessions/${sessionId}/remote-stop`),
  updateProfile: (body: { full_name?: string | null; email?: string | null }) => api.put<User>('/user/profile', body),
  vehicles: () => api.get<{ vehicles: Vehicle[]; total: number }>('/user/vehicles'),
  catalog: () => api.get<{ vehicles: CatalogVehicle[] }>('/public/vehicles'),
  addVehicle: (body: Omit<Vehicle, 'id' | 'created_at'>) => api.post<Vehicle>('/user/vehicles', body),
  updateVehicle: (id: number, body: Partial<Omit<Vehicle, 'id'>>) => api.put<Vehicle>(`/user/vehicles/${id}`, body),
  deleteVehicle: (id: number) => api.del(`/user/vehicles/${id}`),
  notificationPrefs: () => api.get<NotificationPrefs>('/user/notification-preferences'),
  setNotificationPrefs: (body: NotificationPrefs) => api.put<NotificationPrefs>('/user/notification-preferences', body),
  closeAccount: () => api.del('/user/account'),
}

// ── Hooks ───────────────────────────────────────────────────────────────
export const useStations = (q: MapQuery = {}) => useQuery({ queryKey: ['map', q], queryFn: ({ signal }) => map.stations(q, signal), refetchInterval: 60_000, placeholderData: (p) => p })
export const useBalance = (enabled = true) => useQuery({ queryKey: ['balance'], queryFn: driver.balance, enabled })
export const useActive = (enabled = true) =>
  useQuery({ queryKey: ['active'], queryFn: driver.active, enabled, refetchInterval: (q) => (q.state.data?.active_sessions.length ? 5_000 : 30_000) })

/** A mutation that shows a toast and refreshes the given queries. */
export function useAction<TArgs, TResult = unknown>(
  fn: (args: TArgs) => Promise<TResult>,
  opts: { success?: string | ((r: TResult) => string); invalidate?: QueryKey[]; onSuccess?: (r: TResult) => void; quietError?: boolean } = {},
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (result) => {
      const msg = typeof opts.success === 'function' ? opts.success(result) : opts.success
      if (msg) toast.success(msg)
      opts.invalidate?.forEach((key) => qc.invalidateQueries({ queryKey: key }))
      opts.onSuccess?.(result)
    },
    onError: (e) => {
      if (!opts.quietError) toast.error(errorText(e))
    },
  })
}
